import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  FileItem,
  FileProcessingStatus,
  FileStepStatus,
  FileProcessingState as State,
} from '@/types'

interface FileProcessingEntry extends FileProcessingStatus {
  /** When this entry was written, used to drop responses that lost a race. */
  receivedAt: number
}

export type FileProcessingLookup = Record<number, FileProcessingStatus>

/**
 * Current processing state for a file, preferring what the poll last saw.
 *
 * The file lists hold whatever the folder returned when it loaded, and nothing rewrites
 * those rows as processing finishes — the poll deliberately writes here instead, so that
 * one request can serve every surface. Anything deciding whether a file is finished has
 * to merge the two, or it will act on a snapshot that is minutes stale.
 */
export function resolveProcessingState(
  file: FileItem,
  byId: FileProcessingLookup
): State | undefined {
  return byId[file.id]?.processing_state ?? file.processing_state
}

/**
 * Whether the AI features can read this file's text yet.
 *
 * Smart rename and smart organize decide from extracted text, so this tracks OCR alone —
 * a file whose summary or embedding failed is still perfectly renameable.
 */
export function isReadyForAiActions(file: FileItem, byId: FileProcessingLookup): boolean {
  const ocrStatus: FileStepStatus = byId[file.id]?.ocr_status ?? file.ocr_status

  return ocrStatus === 'completed'
}

interface FileProcessingState {
  byId: Record<number, FileProcessingEntry>
}

const initialState: FileProcessingState = {
  byId: {},
}

/**
 * Live processing state for files, laid over whatever the file lists already hold.
 *
 * Kept out of `filesSlice` on purpose: the recently-viewed list feeds `FileTable` from its
 * own records rather than `byFolderId`, and rows move between folder buckets when they are
 * dragged. Keying purely by file id means one poll serves every surface, and a row that
 * moves keeps its badge.
 */
const fileProcessingSlice = createSlice({
  name: 'fileProcessing',
  initialState,
  reducers: {
    statusesReceived: (state, action: PayloadAction<FileProcessingStatus[]>) => {
      const receivedAt = Date.now()

      action.payload.forEach((status) => {
        const existing = state.byId[status.id]

        // A folder refetch can land mid-poll carrying an older view of the same file.
        // Without this a settled row would flicker back to a spinner.
        if (existing && existing.receivedAt > receivedAt) return

        state.byId[status.id] = { ...status, receivedAt }
      })
    },

    clearProcessingStatuses: (state) => {
      state.byId = {}
    },
  },
})

export const { statusesReceived, clearProcessingStatuses } = fileProcessingSlice.actions

export default fileProcessingSlice.reducer
